<template>
  <!-- The wrapper is what the page sizes; the canvas inside is always drawn at
       its natural size and scaled to fit, so a card thumbnail and the live
       preview are the same render at two sizes rather than two markups. -->
  <div ref="frame" class="yzt-frame">
    <!-- The sizer carries the *scaled* dimensions, so the page lays out around
         the rendered size and the canvas can be centred when it is capped below
         its container's width. The canvas is drawn at its natural size inside
         it and scaled from the top-left corner. It is also the card itself —
         the box behind the photo and the names, in the design's own colours. -->
    <div
      class="yzt-sizer"
      :class="{ 'yzt-sizer--transparent': isTransparentCard }"
      :style="sizerStyle"
    >
      <div
        ref="canvasEl"
        class="yzt-canvas yz-design-canvas"
        :style="canvasStyle"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          ref="contentEl"
          class="yzt-content"
          :style="contentStyle"
          v-html="renderedHtml"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  YURTZEITEN_CARD_BACKGROUND_COLOR_DEFAULT_N,
  YURTZEITEN_CARD_BORDER_COLOR_DEFAULT_N,
  YURTZEITEN_CARD_PADDING_DEFAULT_N,
  YURTZEITEN_CANVAS_MIN_CONTENT_HEIGHT,
  YURTZEITEN_COMMENT_COPY_ATTR,
  YURTZEITEN_COMMENT_SCROLLING_CLASS,
  YURTZEITEN_COMMENT_TRACK_ATTR,
  YURTZEITEN_COMMENT_VIEWPORT_ATTR,
  YURTZEITEN_TEMPLATE_CONTENT_WIDTH,
  YURTZEITEN_TEMPLATE_CANVAS,
  findYurtzeitenTemplate_N,
  getYurtzeitenCardPaddingParts_N,
  isTransparentYurtzeitenColor_N,
  normalizeYurtzeitenCardPadding_N,
  normalizeYurtzeitenContentWidth_N,
  normalizeYurtzeitenColor_N,
} from '@/utils/yurtzeiten_design_templates';
import {
  getYurtzeitenCardCapacity_N,
  renderYurtzeitenDesignHtml_N,
} from '@/utils/yurtzeiten_design_render';
import { yurtzeitenFullName } from '@/utils/yurtzeiten_name';
import '@/assets/style/yurtzeiten_design_canvas.css';

/* ---------------------- the note's scrolling window -----------------------
 * The numbers behind `applyCommentScrollers` below. See the block comment there
 * for what the window is and when a note is given one.
 */

// The shortest window a note is ever given. Under about two lines a scrolling
// note reads as a ticker rather than as a note, so a card that cannot spare
// even that keeps its overflow and lets the artboard's own fit take it.
const MIN_COMMENT_WINDOW_PX = 28;

// The blank run between the end of the note and its return, in lines of the
// note's own text — enough that the loop reads as a restart rather than as the
// note running into itself.
const COMMENT_LOOP_GAP_LINES = 2;
const MIN_COMMENT_LOOP_GAP_PX = 16;

// `line-height` from the canvas stylesheet, for text that reports none of its
// own (an inline element inside an unstyled block).
const COMMENT_LINE_HEIGHT_RATIO = 1.25;

// The pace, in frame pixels per second, for a card rendered outside a screen —
// the Design tab's previews, which pass no speed. Matches Display.vue's own
// default so a note reads at one speed wherever it is drawn.
const DEFAULT_COMMENT_SCROLL_SPEED = 25;

// Floor under one lap, in seconds. Deliberately not the artboard's own 24s
// floor: that one covers a whole screen of content restarting, while a lap here
// is a few lines of one note, and stretching that over 24 seconds leaves text
// that looks stopped rather than slow — which reads as a fault, not as respect.
const MIN_COMMENT_SCROLL_DURATION = 6;

export default {
  name: 'Yurtzeiten_Template_Render',
  props: {
    templateId: {
      type: String,
      required: true,
    },
    // The design authored in the editor: { html }.
    settings: {
      type: Object,
      required: true,
    },
    // Real records, already reduced to { english, hebrew, gregorian, hebrewDate,
    // image }. A short list is padded with placeholders so the design still
    // reads the way it will on a full screen.
    entries: {
      type: Array,
      default: () => [],
    },
    // Upper scale limit. Cards are still capped at 1:1 so padding changes keep
    // changing the visible border box instead of being hidden by upscaling.
    maxScale: {
      type: Number,
      default: 1,
    },
    // Editor previews intentionally pad a template with sample rows. The live
    // display disables this so partial cards contain real records only.
    showPlaceholders: {
      type: Boolean,
      default: true,
    },
    // The height this card has to fit into, in the units its own frame is laid
    // out in — the artboard's height, measured by the display and handed down.
    //
    // Zero means there is no artboard to overflow (the Design tab's previews),
    // and the note is then drawn at its full height however long it runs.
    availableHeight: {
      type: Number,
      default: 0,
    },
    // The pace every scroller on the screen shares, in those same units per
    // second, so a note travels at the speed the artboards around it do.
    scrollSpeed: {
      type: Number,
      default: 0,
    },
  },
  // The grid around this card sizes its columns from what the card actually
  // measures, so the gap between cards stays the authored spacing instead of the
  // column's leftover width.
  emits: ['natural-width'],
  data() {
    return {
      scale: 1,
      // The design's real border-box at 1:1, measured from the rendered canvas.
      // Seeded with the original card shape so the first paint has stable space;
      // the next animation frame shrinks it to the actual content plus padding.
      naturalCardWidth: YURTZEITEN_TEMPLATE_CANVAS.width,
      naturalCardHeight: YURTZEITEN_TEMPLATE_CANVAS.height,
    };
  },
  computed: {
    template() {
      return findYurtzeitenTemplate_N(this.templateId);
    },
    canvas() {
      return YURTZEITEN_TEMPLATE_CANVAS;
    },
    // The card's own colours, from the design. Normalised here rather than
    // trusted: the hex fields are free text, so a half-typed value would
    // otherwise reach the canvas as an invalid style.
    cardBorderColor() {
      return normalizeYurtzeitenColor_N(
        this.settings?.borderColor,
        YURTZEITEN_CARD_BORDER_COLOR_DEFAULT_N
      );
    },
    cardBackgroundColor() {
      return normalizeYurtzeitenColor_N(
        this.settings?.backgroundColor,
        YURTZEITEN_CARD_BACKGROUND_COLOR_DEFAULT_N
      );
    },
    cardPadding() {
      return normalizeYurtzeitenCardPadding_N(
        this.settings?.cardPadding ?? YURTZEITEN_CARD_PADDING_DEFAULT_N
      );
    },
    cardPaddingParts() {
      return getYurtzeitenCardPaddingParts_N(this.cardPadding);
    },
    maximumCardWidth() {
      return YURTZEITEN_TEMPLATE_CONTENT_WIDTH + this.cardPaddingParts.horizontal * 2;
    },
    // Whether the design asks to sit straight on whatever is behind it, which
    // the page uses to drop the drop-shadow it would otherwise cast.
    isTransparentCard() {
      return isTransparentYurtzeitenColor_N(this.cardBackgroundColor);
    },
    // Tracks the measured content, so a design taller than the nominal canvas
    // makes the card grow instead of being cut off at the bottom.
    //
    // The card's colours live here rather than on the scaled canvas inside it:
    // this box is drawn at screen scale, so the border stays a crisp 1px and
    // matches the rounded corners it is clipped to at every thumbnail size.
    sizerStyle() {
      return {
        width: `${this.naturalCardWidth * this.scale}px`,
        height: `${this.naturalCardHeight * this.scale}px`,
        backgroundColor: this.cardBackgroundColor,
        borderColor: this.cardBorderColor,
      };
    },
    canvasStyle() {
      const contentWidth = normalizeYurtzeitenContentWidth_N(this.settings?.contentWidth);
      return {
        maxWidth: `${this.maximumCardWidth}px`,
        padding: this.cardPadding,
        '--yz-card-inline-padding': `${this.cardPaddingParts.horizontal}px`,
        '--yz-authored-content-width': contentWidth ? `${contentWidth}px` : '100%',
        transform: `scale(${this.scale})`,
      };
    },
    contentStyle() {
      const width = normalizeYurtzeitenContentWidth_N(this.settings?.contentWidth);
      return {
        minHeight: `${YURTZEITEN_CANVAS_MIN_CONTENT_HEIGHT}px`,
        ...(width ? { width: `${width}px` } : {}),
      };
    },
    // How many people this card draws: one per record block the author left in
    // the design. The template's nominal count is only its starting shape — an
    // author who deleted two of three blocks built a one-person card, and
    // padding to three would render sample rows nothing can ever fill.
    cardCapacity() {
      return getYurtzeitenCardCapacity_N((this.settings && this.settings.html) || '');
    },
    // The entries the design is rendered against — real records first, padded
    // to the number of blocks the design actually carries.
    resolvedEntries() {
      const wanted = this.cardCapacity;
      const filled = this.entries.slice(0, wanted).map((entry) => {
        const textValue = (value, placeholder) =>
          String(value ?? '').trim() || (this.showPlaceholders ? placeholder : '');
        // The two halves are the record's own; the whole name falls back to
        // joining them so a design mixing {englishName} with {firstName} draws
        // the same person either way.
        const firstName = textValue(entry?.firstName, 'First');
        const lastName = textValue(entry?.lastName, 'Name');
        const englishName = textValue(
          entry?.english
            || entry?.englishName
            || entry?.name
            || yurtzeitenFullName(entry?.firstName, entry?.lastName),
          'English Name'
        );
        const hebrewName = textValue(entry?.hebrew || entry?.hebrewName, 'Hebrew Name');
        const gregorianDate = textValue(entry?.gregorian || entry?.gregorianDate, '—');
        const hebrewDate = textValue(entry?.hebrewDate, '—');
        const comment = textValue(entry?.comment ?? entry?.notes, 'Comment');

        return {
          englishName,
          firstName,
          lastName,
          hebrewName,
          gregorianDate,
          hebrewDate,
          comment,
          image: entry?.image || null,
          english: englishName,
          hebrew: hebrewName,
          gregorian: gregorianDate,
          name: englishName,
        };
      });

      while (this.showPlaceholders && filled.length < wanted) {
        filled.push({
          englishName: 'English Name',
          firstName: 'First',
          lastName: 'Name',
          hebrewName: 'Hebrew Name',
          gregorianDate: '—',
          hebrewDate: '—',
          comment: 'Comment',
          english: 'English Name',
          hebrew: 'Hebrew Name',
          gregorian: '—',
          name: 'English Name',
          image: null,
        });
      }

      return filled;
    },
    // Only one photo is shown, so it comes off the first record that has one
    // rather than leaving the frame empty when the newest record has none.
    leadImage() {
      return this.entries.find((entry) => entry?.image)?.image || null;
    },
    // The authored HTML with its variables resolved. The algorithm lives in a
    // util so it can be exercised without mounting a component.
    renderedHtml() {
      return renderYurtzeitenDesignHtml_N({
        html: (this.settings && this.settings.html) || '',
        entries: this.resolvedEntries,
        leadImage: this.leadImage,
        removeUnfilledEntries: !this.showPlaceholders,
      });
    },
  },
  methods: {
    // Measuring writes the sizer's height, which resizes the frame that the
    // same observer watches. Doing that inside the observer callback is what
    // makes the browser report "ResizeObserver loop completed with undelivered
    // notifications" — the work is pushed to the next frame instead, and a
    // burst of notifications collapses into a single pass.
    scheduleMeasure() {
      // `measureFrame` is a plain instance property, like `resizeObserver`
      // below: it is scheduling bookkeeping, and making it reactive state would
      // queue a render for nothing.
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    },
    measure() {
      const frame = this.$refs.frame;
      const canvasEl = this.$refs.canvasEl;
      if (!frame || !canvasEl) return;

      const width = frame.clientWidth;
      const measuredWidth = Math.ceil(canvasEl.offsetWidth || canvasEl.scrollWidth || 0);
      const nextCardWidth = Math.max(measuredWidth, 1);

      if (nextCardWidth !== this.naturalCardWidth) this.naturalCardWidth = nextCardWidth;
      // The unscaled width of the design itself — independent of how wide the
      // frame is, so reporting it can't feed back into the fit it drives.
      if (nextCardWidth !== this.reportedCardWidth) {
        this.reportedCardWidth = nextCardWidth;
        this.$emit('natural-width', nextCardWidth);
      }

      // Zero while the tab is hidden — the last good scale is kept rather than
      // collapsing the canvas, and the observer re-measures once it is shown.
      let nextScale = this.scale;
      if (width) {
        const maxScale = Math.min(Number(this.maxScale) || 1, 1);
        nextScale = Math.min(width / nextCardWidth, maxScale);
      }

      // Before the height is read rather than after: a note given a window is
      // the reason the card has the height it is about to be drawn at, and the
      // two have to be settled in one pass or the card is sized for a design it
      // no longer holds.
      this.applyCommentScrollers(canvasEl, nextScale);

      const measuredHeight = Math.ceil(canvasEl.offsetHeight || canvasEl.scrollHeight || 0);
      const nextCardHeight = Math.max(measuredHeight, 1);
      if (nextCardHeight !== this.naturalCardHeight) this.naturalCardHeight = nextCardHeight;

      // Assigned only on a real change: writing the same value would re-render
      // and hand the observer another notification for nothing.
      if (nextScale !== this.scale) this.scale = nextScale;
    },
    /* --------------------- the note's scrolling window --------------------
     *
     * A card too tall for its artboard is otherwise shrunk (a no-scroll
     * artboard) or cut off (every other kind), and the note is nearly always
     * what made it too tall — a name and a date have a length to them, a note
     * does not. So the note alone is given a window of the height the card can
     * spare, and its text is run past that window on a loop, the way credits
     * run, while the photo, the names and the dates keep their size and stay
     * exactly where the design put them.
     *
     * Every window this method applied is cleared before anything is read, so
     * the decision is always taken from the design at its natural height. That
     * is what keeps it still: a card shortened to fit would otherwise measure
     * as fitting, be let out again, overflow again, and flicker between the two
     * for as long as the board was up.
     *
     * Called from `measure` and nowhere else — see the note there.
     */
    applyCommentScrollers(canvasEl, scale) {
      const viewports = Array.from(
        canvasEl.querySelectorAll(`[${YURTZEITEN_COMMENT_VIEWPORT_ATTR}]`)
      );
      if (!viewports.length) return;

      // A card the artboard is not currently showing measures as nothing, and
      // a window taken from that would be nonsense. What was measured while it
      // was up is kept instead: the observer re-measures on the way back, and
      // until it does the note is drawn as it was last seen rather than
      // flashing out to its full height for a frame.
      if (!canvasEl.offsetHeight) return;

      viewports.forEach((viewport) => this.resetCommentScroller(viewport));

      // The budget arrives in the frame's units; the design is drawn inside the
      // scale below it, so it is converted here, once.
      const available = Number(this.availableHeight) || 0;
      const budget = available > 0 && scale > 0 ? available / scale : 0;
      if (!budget) return;

      // A pixel of tolerance, so a card that fits exactly is never set moving
      // by sub-pixel rounding.
      const overflow = (canvasEl.offsetHeight || 0) - budget;
      if (overflow <= 1) return;

      // Every note is read before any of them is written to: a window applied
      // here would change the layout the next measurement is taken from.
      const notes = viewports
        .map((viewport) => {
          const copies = Array.from(
            viewport.querySelectorAll(`[${YURTZEITEN_COMMENT_COPY_ATTR}]`)
          );
          const [firstCopy] = copies;
          return {
            viewport,
            copies,
            track: viewport.querySelector(`[${YURTZEITEN_COMMENT_TRACK_ATTR}]`),
            naturalHeight: firstCopy ? firstCopy.offsetHeight : 0,
            lineHeight: this.readNoteLineHeight(firstCopy),
          };
        })
        .filter((note) => note.track && note.naturalHeight > 0);
      if (!notes.length) return;

      const totalNatural = notes.reduce((total, note) => total + note.naturalHeight, 0);
      // What the notes have to come down to between them for the card to fit.
      // Shared out in proportion to how tall each one is, so on a card drawing
      // several records the long note gives up nearly all of it and the
      // one-line note beside it is left whole.
      const targetTotal = Math.max(totalNatural - overflow, 0);

      notes.forEach((note) => {
        const share = totalNatural > 0 ? note.naturalHeight / totalNatural : 0;
        const floor = Math.min(
          note.naturalHeight,
          Math.max(Math.round(note.lineHeight * 2), MIN_COMMENT_WINDOW_PX)
        );
        const windowHeight = Math.max(Math.round(targetTotal * share), floor);
        // A note that fits the height it was given has nothing to scroll, and
        // moving it would be motion for its own sake. Whatever it could not
        // give up stays with the card, for the artboard's own fit to take.
        if (windowHeight >= note.naturalHeight - 1) return;
        this.startCommentScroller(note, windowHeight, scale);
      });
    },
    // Back to the authored note: no window, no clipping, no loop. Every pass
    // starts here, so nothing below ever measures this method's own work.
    resetCommentScroller(viewport) {
      viewport.classList.remove(YURTZEITEN_COMMENT_SCROLLING_CLASS);
      viewport.style.height = '';
      viewport
        .querySelectorAll(`[${YURTZEITEN_COMMENT_COPY_ATTR}]`)
        .forEach((copy) => {
          copy.style.paddingBottom = '';
        });
      const track = viewport.querySelector(`[${YURTZEITEN_COMMENT_TRACK_ATTR}]`);
      if (track) track.style.animationDuration = '';
    },
    startCommentScroller(note, windowHeight, scale) {
      const gap = Math.max(
        Math.round(note.lineHeight * COMMENT_LOOP_GAP_LINES),
        MIN_COMMENT_LOOP_GAP_PX
      );
      // One lap: the note, then the blank run that separates it from its
      // return. Both copies carry the same run, so half the track is exactly
      // one lap — which is the distance the animation travels.
      const lap = note.naturalHeight + gap;

      note.copies.forEach((copy) => {
        copy.style.paddingBottom = `${gap}px`;
      });
      note.viewport.style.height = `${windowHeight}px`;

      const speed = Number(this.scrollSpeed) > 0
        ? Number(this.scrollSpeed)
        : DEFAULT_COMMENT_SCROLL_SPEED;
      // The lap is in the design's units and the speed is in the frame's, so
      // the distance is converted before it is divided: a card drawn at half
      // size covers half the ground, and reads at the pace of the artboards
      // beside it rather than at twice their speed.
      const duration = Math.max((lap * scale) / speed, MIN_COMMENT_SCROLL_DURATION);
      note.track.style.animationDuration = `${duration}s`;

      note.viewport.classList.add(YURTZEITEN_COMMENT_SCROLLING_CLASS);
    },
    // The note's own line box, taken from the tallest line inside it. The editor
    // writes a font size onto the text rather than onto the block around it, so
    // the block's own line-height is not the one the reader sees.
    readNoteLineHeight(copy) {
      if (!copy || typeof window === 'undefined') return 0;

      return [copy, ...copy.querySelectorAll('*')].reduce((tallest, element) => {
        const style = window.getComputedStyle(element);
        const lineHeight = parseFloat(style.lineHeight);
        const value = Number.isFinite(lineHeight)
          ? lineHeight
          : (parseFloat(style.fontSize) || 0) * COMMENT_LINE_HEIGHT_RATIO;
        return value > tallest ? value : tallest;
      }, 0);
    },
    // An <img> carries a width but no height, so its real height is only known
    // once it loads — and that is exactly when a design grows past the nominal
    // canvas. ResizeObserver catches it, this is the fallback for browsers
    // without one.
    watchImages() {
      const canvasEl = this.$refs.canvasEl;
      if (!canvasEl) return;
      canvasEl.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', this.scheduleMeasure, { once: true });
        img.addEventListener('error', this.scheduleMeasure, { once: true });
      });
    },
  },
  watch: {
    // Re-measure after the new design has actually been painted.
    renderedHtml() {
      this.$nextTick(() => {
        this.scheduleMeasure();
        this.watchImages();
      });
    },
    // The artboard can change height — a screen resized, a board moved to
    // another artboard — without this card's own box changing at all, so the
    // observer below never hears about it. The note's window is measured
    // against that height, so it is re-measured when the height arrives.
    availableHeight() {
      this.scheduleMeasure();
    },
    scrollSpeed() {
      this.scheduleMeasure();
    },
  },
  mounted() {
    this.measure();
    this.watchImages();
    // The rail, the tab, and the browser window all resize this box, so the fit
    // is observed rather than computed once. The canvas is observed too: that
    // is what reports the design growing — an image finishing loading, a font
    // swapping in, or the author adding a line.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this.$refs.frame);
      this.resizeObserver.observe(this.$refs.canvasEl);
    } else {
      this.onWindowResize = this.scheduleMeasure;
      window.addEventListener('resize', this.onWindowResize);
    }
  },
  beforeUnmount() {
    if (this.measureFrame) {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize);
      this.onWindowResize = null;
    }
  },
};
</script>

<style scoped>
.yzt-frame {
  width: 100%;
}

.yzt-sizer {
  position: relative;
  margin: 0 auto;
  /* Clips to the rounded corners drawn on this box. It can no longer cut the
     design off: the height above tracks the measured content. */
  overflow: hidden;
  /* The card. `border-color` and `background-color` come from the design, so
     the width is stated here and never changes — a transparent border still
     occupies its 1px and the layout can't shift when the colour does.
     Deliberately left at the default `content-box`: the scaled canvas is sized
     to this box's content area, so the border sits outside it and can never
     clip the design's edge. */
  border: 1px solid transparent;
  border-radius: var(--radius-md);
}

.yzt-canvas {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  padding: 24px 28px;
  width: max-content;
  max-width: 560px;
  box-sizing: border-box;
  /* The card's colour is painted by the sizer above — this box only carries
     the design, so it stays clear of it. */
  background: transparent;
}

/* Old designs use their intrinsic max-content width. Newly authored designs
   receive the editor's measured width inline, so filling variables or swapping
   the record photo cannot change the card's authored proportions. */
.yzt-content {
  width: max-content;
  max-width: 100%;
}

</style>
